import { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Lock,
  Eye,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  XCircle,
  HelpCircle,
  MapPin,
  Shield,
  FileText,
  Users,
  ClipboardCheck,
  Briefcase,
  Building,
  HeartPulse,
  UserCheck,
  Home,
  Stethoscope,
  FolderOpen,
  ClipboardList,
  GraduationCap,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Container } from '../components/layout/Container';
import { Button } from '../components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { Select } from '../components/ui/Select';
import { Progress } from '../components/ui/Progress';
import { getChecklistForState, SCORING_CONFIG, requiresStateLicense } from '../config/checklist';
import { getStateOptions, getStateByCode, type ChecklistCategory } from '../config/states';
import { cn } from '../lib/utils';
import { trackEvent } from '../lib/api';

// ============================================
// CONSTANTS
// ============================================

/** Label for the CTA button */
const CTA_LABEL = 'Free';

// ============================================
// TYPES
// ============================================

interface CategoryIconMapping {
  icon: React.ElementType;
  color: string;
  bgColor: string;
}

// ============================================
// CATEGORY ICON MAPPINGS
// ============================================

/**
 * Maps category IDs to their icons and colors
 * Supports both legacy format (licensing, personnel, etc.)
 * and new diagnostic format (A, B, C section prefixes)
 */
const categoryIconMappings: Record<string, CategoryIconMapping> = {
  // Legacy format IDs
  'licensing': { icon: Building, color: '#009688', bgColor: 'rgba(0, 150, 136, 0.1)' },
  'personnel': { icon: Users, color: '#009688', bgColor: 'rgba(0, 150, 136, 0.1)' },
  'training': { icon: GraduationCap, color: '#009688', bgColor: 'rgba(0, 150, 136, 0.1)' },
  'documentation': { icon: FileText, color: '#009688', bgColor: 'rgba(0, 150, 136, 0.1)' },
  'operations': { icon: Briefcase, color: '#009688', bgColor: 'rgba(0, 150, 136, 0.1)' },
  'client-care': { icon: HeartPulse, color: '#009688', bgColor: 'rgba(0, 150, 136, 0.1)' },
  'safety': { icon: Shield, color: '#009688', bgColor: 'rgba(0, 150, 136, 0.1)' },

  // New diagnostic format - Section letters
  // Section A: Licensing & Governance
  'A': { icon: Building, color: '#009688', bgColor: 'rgba(0, 150, 136, 0.1)' },
  'licensing-governance': { icon: Building, color: '#009688', bgColor: 'rgba(0, 150, 136, 0.1)' },

  // Section B: Background Checks
  'B': { icon: UserCheck, color: '#009688', bgColor: 'rgba(0, 150, 136, 0.1)' },
  'background-checks': { icon: UserCheck, color: '#009688', bgColor: 'rgba(0, 150, 136, 0.1)' },

  // Section C: Personnel
  'C': { icon: Users, color: '#009688', bgColor: 'rgba(0, 150, 136, 0.1)' },

  // Section D: Home Health Aide
  'D': { icon: HeartPulse, color: '#009688', bgColor: 'rgba(0, 150, 136, 0.1)' },
  'home-health-aide': { icon: HeartPulse, color: '#009688', bgColor: 'rgba(0, 150, 136, 0.1)' },

  // Section E: Homemaker Services
  'E': { icon: Home, color: '#009688', bgColor: 'rgba(0, 150, 136, 0.1)' },
  'homemaker-services': { icon: Home, color: '#009688', bgColor: 'rgba(0, 150, 136, 0.1)' },

  // Section F: Care Delivery
  'F': { icon: Stethoscope, color: '#009688', bgColor: 'rgba(0, 150, 136, 0.1)' },
  'care-delivery': { icon: Stethoscope, color: '#009688', bgColor: 'rgba(0, 150, 136, 0.1)' },

  // Section G: Client Records
  'G': { icon: FolderOpen, color: '#009688', bgColor: 'rgba(0, 150, 136, 0.1)' },
  'client-records': { icon: FolderOpen, color: '#009688', bgColor: 'rgba(0, 150, 136, 0.1)' },

  // Section H: Survey & Compliance
  'H': { icon: ClipboardList, color: '#009688', bgColor: 'rgba(0, 150, 136, 0.1)' },
  'survey-compliance': { icon: ClipboardList, color: '#009688', bgColor: 'rgba(0, 150, 136, 0.1)' },

  // Section I: Training
  'I': { icon: GraduationCap, color: '#009688', bgColor: 'rgba(0, 150, 136, 0.1)' },
};

/**
 * Get icon mapping for a category, with fallback
 */
function getCategoryIconMapping(categoryId: string): CategoryIconMapping {
  // Try exact match first
  if (categoryIconMappings[categoryId]) {
    return categoryIconMappings[categoryId];
  }

  // Try first letter (for diagnostic format like "A1", "B2")
  const firstLetter = categoryId.charAt(0).toUpperCase();
  if (categoryIconMappings[firstLetter]) {
    return categoryIconMappings[firstLetter];
  }

  // Fallback
  return { icon: ClipboardCheck, color: '#009688', bgColor: 'rgba(0, 150, 136, 0.1)' };
}

// ============================================
// STATUS OPTIONS
// ============================================

const statusOptions = [
  {
    value: 'COMPLIANT',
    label: 'Compliant',
    icon: CheckCircle,
    color: 'text-green-600',
    points: SCORING_CONFIG.compliantPoints,
    description: 'Meets all requirements',
  },
  {
    value: 'AT_RISK',
    label: 'At Risk',
    icon: AlertTriangle,
    color: 'text-yellow-600',
    points: SCORING_CONFIG.atRiskPoints,
    description: 'Needs attention soon',
  },
  {
    value: 'NON_COMPLIANT',
    label: 'Non-Compliant',
    icon: XCircle,
    color: 'text-red-600',
    points: SCORING_CONFIG.nonCompliantPoints,
    description: 'Immediate action required',
  },
  {
    value: 'UNKNOWN',
    label: 'Unknown',
    icon: HelpCircle,
    color: 'text-gray-500',
    points: SCORING_CONFIG.unknownPoints,
    description: 'Status not yet determined',
  },
];

// ============================================
// COMPONENT
// ============================================

export function Preview() {
  // Get state from URL params if present (e.g., /preview/CA or /preview?state=CA)
  const { stateCode: paramStateCode } = useParams<{ stateCode?: string }>();
  const [searchParams] = useSearchParams();
  const queryStateCode = searchParams.get('state');

  const [selectedState, setSelectedState] = useState(paramStateCode || queryStateCode || '');
  const [error, setError] = useState<string | null>(null);
  const isLoading = false; // Static - no async loading needed

  useEffect(() => {
    trackEvent({ event: 'preview_page_view', properties: { state: paramStateCode || queryStateCode || null } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Update selected state if URL params change
  useEffect(() => {
    const stateFromUrl = paramStateCode || queryStateCode;
    if (stateFromUrl && stateFromUrl !== selectedState) {
      setSelectedState(stateFromUrl.toUpperCase());
    }
  }, [paramStateCode, queryStateCode]);

  // Fetch checklist data
  const stateOptions = getStateOptions();
  const selectedStateInfo = selectedState ? getStateByCode(selectedState) : null;
  const requiresLicense = selectedState ? requiresStateLicense(selectedState) : true;

  let checklist: ChecklistCategory[] = [];
  try {
    checklist = selectedState ? getChecklistForState(selectedState) : [];
  } catch (err) {
    console.error('Error loading checklist:', err);
    setError('Failed to load checklist for this state.');
  }

  const totalItems = checklist.reduce((sum, cat) => sum + cat.items.length, 0);
  const totalHighSeverity = checklist.reduce(
    (sum, cat) => sum + cat.items.filter(i => i.severity === 'high').length,
    0
  );
  const totalMediumSeverity = checklist.reduce(
    (sum, cat) => sum + cat.items.filter(i => i.severity === 'medium').length,
    0
  );

  // Handle state change
  const handleStateChange = (newState: string) => {
    setError(null);
    setSelectedState(newState);

    // Update URL without navigation
    if (newState) {
      window.history.replaceState(null, '', `/preview?state=${newState}`);
    } else {
      window.history.replaceState(null, '', '/preview');
    }
  };

  // Build checkout URL
  const checkoutUrl = selectedState ? `/checkout?state=${selectedState}` : '/checkout';

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      {/* Preview Banner */}
      <div className="text-white py-2 sm:py-3 mt-16" style={{ backgroundColor: '#009688' }}>
        <Container>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Eye className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
              <span className="font-medium text-sm sm:text-base">Preview Mode</span>
              <span className="text-teal-200 text-sm hidden md:inline">
                — See what's covered in your free assessment
              </span>
            </div>
            <Link to={checkoutUrl} className="shrink-0">
              <Button
                size="sm"
                variant="secondary"
                className="bg-white text-teal-600 hover:bg-teal-50 border-0 text-xs sm:text-sm whitespace-nowrap"
              >
                <span className="hidden sm:inline">Start Free Assessment</span>
                <span className="sm:hidden">Start — {CTA_LABEL}</span>
              </Button>
            </Link>
          </div>
        </Container>
      </div>

      <main className="flex-1 py-8">
        <Container size="lg">
          {/* State Selection */}
          <Card className="mb-8" padding="none">
            <div className="p-4 md:p-5">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                <div className="flex items-center gap-3 flex-1">
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                    style={{ backgroundColor: '#009688' }}
                  >
                    <MapPin className="w-4 h-4 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="font-semibold text-gray-900 text-sm md:text-base">
                      Select Your State
                    </h2>
                    <p className="text-xs md:text-sm text-gray-500 hidden sm:block">
                      Each state has specific compliance requirements
                    </p>
                  </div>
                </div>
                <div className="w-full sm:w-auto sm:min-w-[200px] md:min-w-[250px]">
                  <Select
                    value={selectedState}
                    onChange={e => handleStateChange(e.target.value)}
                    options={[
                      { value: '', label: 'Choose a state...' },
                      ...stateOptions,
                    ]}
                  />
                </div>
              </div>
            </div>
          </Card>

          {/* Error State */}
          {error && (
            <Card className="mb-8 border-red-200 bg-red-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <div>
                    <p className="font-medium text-red-800">{error}</p>
                    <p className="text-sm text-red-600">
                      Please try selecting a different state or contact support.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* No License Required Banner */}
          {selectedState && !requiresLicense && !isLoading && (
            <Card className="mb-8 border-blue-200 bg-blue-50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-800">
                      {selectedStateInfo?.name} Does Not Require State Licensure
                    </p>
                    <p className="text-sm text-blue-700 mt-1">
                      {selectedState === 'AL' &&
                        'Alabama does not require state licensure for home health agencies. Medicare certification is required for agencies providing skilled services.'}
                      {selectedState === 'MI' &&
                        'Michigan does not require a state license for non-medical home care agencies. Medicare certification is required for skilled nursing services.'}
                      {selectedState === 'ID' &&
                        'Idaho eliminated the home health agency license requirement effective July 1, 2025. Medicare certification is still required for skilled services.'}
                    </p>
                    <p className="text-xs text-blue-600 mt-2">
                      This checklist focuses on Medicare certification requirements, business compliance, and industry best practices.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Loading State */}
          {isLoading && (
            <Card className="mb-8">
              <CardContent className="p-8 text-center">
                <Loader2 className="w-8 h-8 mx-auto mb-4 text-teal-600 animate-spin" />
                <p className="text-gray-600">Loading compliance requirements...</p>
              </CardContent>
            </Card>
          )}

          {/* Header */}
          {!isLoading && (
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {selectedStateInfo
                  ? `${selectedStateInfo.name} Home Care Compliance Checklist`
                  : 'Home Care Compliance Risk Checklist'}
              </h1>
              <p className="text-gray-600">
                {selectedStateInfo
                  ? `${totalItems} compliance items across ${checklist.length} categories for ${selectedStateInfo.name}. Reflects current state regulations.`
                  : 'Select your state above to see state-specific compliance requirements.'}
              </p>

              {/* Quick Stats */}
              {selectedState && totalItems > 0 && (
                <div className="flex flex-wrap gap-4 mt-4">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="px-2 py-1 bg-red-100 text-red-700 rounded font-medium">
                      {totalHighSeverity} Critical
                    </span>
                    <span className="text-gray-500">items</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded font-medium">
                      {totalMediumSeverity} Important
                    </span>
                    <span className="text-gray-500">items</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded font-medium">
                      {checklist.length} Categories
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Scoring Info Card */}
          {selectedState && !isLoading && (
            <Card className="mb-8">
              <CardHeader>
                <CardTitle as="h2">How Scoring Works</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">
                      Status Options
                    </h3>
                    <div className="space-y-2">
                      {statusOptions.map(option => (
                        <div key={option.value} className="flex items-center gap-2">
                          <option.icon className={cn('w-5 h-5', option.color)} />
                          <span className="text-gray-700">{option.label}</span>
                          <span className="text-gray-400 text-sm">
                            ({option.points} {option.points === 1 ? 'point' : 'points'})
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h4 className="font-medium text-gray-900 mb-2 text-sm">
                        Severity Multipliers
                      </h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                            low
                          </span>
                          <span className="text-gray-600">
                            {SCORING_CONFIG.severityWeights.low}x multiplier
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">
                            medium
                          </span>
                          <span className="text-gray-600">
                            {SCORING_CONFIG.severityWeights.medium}x multiplier
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">
                            high
                          </span>
                          <span className="text-gray-600">
                            {SCORING_CONFIG.severityWeights.high}x multiplier
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">Risk Bands</h3>
                    <div className="space-y-3">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-green-600 font-medium">Low Risk</span>
                          <span className="text-sm text-gray-500">
                            {SCORING_CONFIG.bands.low.min}-{SCORING_CONFIG.bands.low.max} points
                          </span>
                        </div>
                        <Progress
                          value={SCORING_CONFIG.bands.low.max}
                          max={150}
                          variant="success"
                          size="sm"
                        />
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-yellow-600 font-medium">Medium Risk</span>
                          <span className="text-sm text-gray-500">
                            {SCORING_CONFIG.bands.medium.min}-{SCORING_CONFIG.bands.medium.max} points
                          </span>
                        </div>
                        <Progress
                          value={SCORING_CONFIG.bands.medium.max}
                          max={150}
                          variant="warning"
                          size="sm"
                        />
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-red-600 font-medium">High Risk</span>
                          <span className="text-sm text-gray-500">
                            {SCORING_CONFIG.bands.high.min}+ points
                          </span>
                        </div>
                        <Progress
                          value={150}
                          max={150}
                          variant="danger"
                          size="sm"
                        />
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">
                        <strong>Example:</strong> A "Non-Compliant" item with "high" severity
                        = {SCORING_CONFIG.nonCompliantPoints} × {SCORING_CONFIG.severityWeights.high}
                        = <strong>{SCORING_CONFIG.nonCompliantPoints * SCORING_CONFIG.severityWeights.high} points</strong>
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Categories Grid */}
          {selectedState && !isLoading ? (
            checklist.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {checklist.map(category => {
                  const iconMapping = getCategoryIconMapping(category.id);
                  const IconComponent = iconMapping.icon;
                  const highSeverityCount = category.items.filter(i => i.severity === 'high').length;
                  const mediumSeverityCount = category.items.filter(i => i.severity === 'medium').length;

                  return (
                    <Card
                      key={category.id}
                      className="hover:shadow-md transition-shadow cursor-default"
                    >
                      <CardContent className="p-5">
                        <div className="flex items-start gap-4">
                          <div
                            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: iconMapping.bgColor }}
                          >
                            <IconComponent
                              className="w-6 h-6"
                              style={{ color: iconMapping.color }}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 mb-1">
                              {category.name}
                            </h3>
                            {category.description && (
                              <p className="text-xs text-gray-500 mb-2 line-clamp-2">
                                {category.description}
                              </p>
                            )}
                            <p className="text-sm text-gray-500 mb-3">
                              {category.items.length} compliance item{category.items.length !== 1 ? 's' : ''}
                            </p>
                            <div className="flex items-center gap-2 flex-wrap">
                              {highSeverityCount > 0 && (
                                <span className="px-2 py-0.5 text-xs font-medium rounded bg-red-100 text-red-700">
                                  {highSeverityCount} critical
                                </span>
                              )}
                              {mediumSeverityCount > 0 && (
                                <span className="px-2 py-0.5 text-xs font-medium rounded bg-yellow-100 text-yellow-700">
                                  {mediumSeverityCount} important
                                </span>
                              )}
                              <Lock className="w-3.5 h-3.5 text-gray-300 ml-auto" />
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              /* Empty State - State selected but no checklist items */
              <Card className="text-center py-12">
                <CardContent>
                  <AlertCircle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Checklist Coming Soon
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto mb-4">
                    We're currently building the compliance checklist for {selectedStateInfo?.name}.
                    Check back soon or contact us for updates.
                  </p>
                  <p className="text-sm text-gray-400">
                    State code: {selectedState}
                  </p>
                </CardContent>
              </Card>
            )
          ) : !selectedState && !isLoading ? (
            /* No State Selected */
            <Card className="text-center py-12">
              <CardContent>
                <MapPin className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Select a State to View Checklist
                </h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  Choose your state from the dropdown above to see the specific compliance
                  requirements for your home care agency.
                </p>
              </CardContent>
            </Card>
          ) : null}

          {/* Regulatory Info Card */}
          {selectedState && selectedStateInfo && checklist.length > 0 && !isLoading && (
            <Card className="mt-8">
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: requiresLicense ? 'rgba(0, 150, 136, 0.1)' : 'rgba(59, 130, 246, 0.1)' }}
                  >
                    <Shield className="w-5 h-5" style={{ color: requiresLicense ? '#009688' : '#3B82F6' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      {requiresLicense
                        ? `${selectedStateInfo.name} Regulatory Authority`
                        : `${selectedStateInfo.name} Compliance Framework`
                      }
                    </h3>
                    <p className="text-sm text-gray-600 mb-2">
                      {requiresLicense
                        ? `This checklist reflects current ${selectedStateInfo.name} home care regulations.`
                        : `${selectedStateInfo.name} does not require state licensure. This checklist covers Medicare certification (42 CFR Part 484), business requirements, and industry best practices.`
                      }
                    </p>
                    <p className="text-xs text-gray-500">
                      {requiresLicense
                        ? 'Requirements sourced from state administrative code and licensing regulations.'
                        : 'Requirements sourced from CMS Conditions of Participation and federal regulations.'
                      }
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Bottom CTA */}
          {selectedState && checklist.length > 0 && !isLoading && (
            <div className="mt-12 text-center">
              <Card className="inline-block p-8 max-w-lg">
                <Lock className="w-12 h-12 mx-auto mb-4" style={{ color: '#009688' }} />
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  Ready to Complete Your {selectedStateInfo?.name} Assessment?
                </h2>
                <p className="text-gray-600 mb-4">
                  Unlock all {totalItems} compliance items across {checklist.length} categories.
                  Complete at your own pace and download your professional PDF report.
                </p>
                <ul className="text-sm text-gray-500 mb-6 space-y-1">
                  <li>✓ State-specific diagnostic questions</li>
                  <li>✓ Automated compliance scoring</li>
                  <li>✓ Professional PDF report</li>
                  <li>✓ Priority action recommendations</li>
                </ul>
                <p className="text-sm text-gray-500 mb-6">
                  Reflects current {selectedStateInfo?.name} regulations
                </p>
                <Link to={checkoutUrl} className="block">
                  <button
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-3 rounded-xl text-white font-medium hover:opacity-90 transition text-base sm:text-lg"
                    style={{ backgroundColor: '#009688' }}
                  >
                    <span className="hidden sm:inline">Start Free Assessment</span>
                    <span className="sm:hidden">Start — {CTA_LABEL}</span>
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </Link>
                <p className="text-xs text-gray-400 mt-4">
                  100% free • No payment required
                </p>
              </Card>
            </div>
          )}
        </Container>
      </main>

      <Footer />
    </div>
  );
}

export default Preview;
